import { api } from './Api';
import {
  CustomerSearchResponseETL,
  CustomerDetailETL,
  OrderReceiptETL,
  OrderSearchResponseETL,
} from '../utils/ExtractTransformLoad/CustomerETL';

// All endpoint paths confirmed against the DOP API reference.
// Base: /deliveryops/customers/* and /deliveryops/orders/*

/**
 * Searches for customers by phone, name, or address.
 * Only non-empty params are sent; the API applies priority server-side:
 *   phone → first_name+last_name (both) → first_name only → last_name only → address
 *
 * @param {{ phone?, firstName?, lastName?, address?, page? }} params
 * Endpoint: GET /deliveryops/customers/search
 * Response: { page, results: [...] }
 */
export const searchCustomers = async ({
  phone,
  firstName,
  lastName,
  address,
  page,
} = {}) => {
  const params = {};
  if (phone) params.phone = phone;
  if (firstName) params.first_name = firstName;
  if (lastName) params.last_name = lastName;
  if (address) params.address = address;
  if (page) params.page = page;

  const response = await api.get('/deliveryops/customers/search', params);
  return CustomerSearchResponseETL(response);
};

/**
 * Fetches the full customer profile for a given customer ID.
 * Includes all phones, all addresses (active and inactive), tags, price levels,
 * paginated order history (last 12 months, 20/page), and customer-order errors.
 *
 * @param {number|string} customerId — internal customer ID
 * @param {number} [page=1] — page of order history
 * Endpoint: GET /deliveryops/customers/{customerId}?page=N
 */
export const getCustomerDetail = async (customerId, page = 1) => {
  const response = await api.get(`/deliveryops/customers/${customerId}`, {
    page,
  });
  return CustomerDetailETL(response);
};

/**
 * Fetches a full order receipt by order ID or display ID.
 * All-digit strings are treated by the API as a numeric order ID;
 * any other string is treated as a display ID (e.g. "ABC-123").
 *
 * @param {string} q — numeric order ID or display ID string
 * Endpoint: GET /deliveryops/orders/receipt?order_id=...
 */
export const getOrderReceipt = async (q) => {
  const isNumericId = /^\d+$/.test(q);
  const normalizedQ = isNumericId ? parseInt(q, 10) : q;
  const response = await api.get('/deliveryops/orders/receipt', { order_id: normalizedQ });
  return OrderReceiptETL(response);
};

/**
 * Searches for orders by date/time range and optional restaurant name.
 * At least one of `from` or `to` must be supplied (API enforces this with 400).
 * `restaurant` is an optional case-insensitive substring match.
 * Results are paginated at 20/page, ordered newest-first.
 *
 * API defaulting behaviour when only one timestamp is provided:
 *   from only → to defaults to current server time
 *   to only   → from defaults to to − 86400 s (24 h earlier)
 *
 * @param {{ from?, to?, restaurant?, page? }} params
 *   from, to — Unix timestamps in seconds
 *   restaurant — optional partial name (e.g. "dusty" matches "Dusty Boot")
 *   page — page number (default 1)
 * Endpoint: GET /deliveryops/orders/search
 * Response: { total_results, total_pages, page, results: [...] }
 */
export const searchOrders = async ({
  from,
  to,
  restaurant,
  page,
} = {}) => {
  const params = {};
  if (from != null) params.from = from;
  if (to != null) params.to = to;
  if (restaurant) params.restaurant = restaurant;
  if (page) params.page = page;

  const response = await api.get('/deliveryops/orders/search', params);
  return OrderSearchResponseETL(response);
};

export const updateOrderDeliveryDetails = async (orderId, payload) => {
  return api.patch(`/deliveryops/orders/${orderId}/delivery-details`, payload);
};
